import { capitalize } from '@tunasong/models'
import { entityTypeNames, type EntityType } from '@tunasong/schemas'
import { entityTypeLabel } from '@tunasong/ui-lib'
import type { ComponentType } from 'react'
import { withBlockErrorBoundary } from './block-error-boundary.js'

/** The parts of a (Tuna or Plate) plugin this reads */
interface GuardablePlugin {
  key: string
  description?: string
  node?: { type?: string; isLeaf?: boolean; isInline?: boolean; component?: unknown }
  plugins?: GuardablePlugin[]
}

const isEntityType = (type: string): type is EntityType => (entityTypeNames as readonly string[]).includes(type)

/** The block type as people read it: the entity type's label, else the plugin's description, else its key */
const labelOf = (plugin: GuardablePlugin) => {
  const type = plugin.node?.type ?? plugin.key
  return isEntityType(type) ? entityTypeLabel(type) : (plugin.description ?? capitalize(type))
}

/**
 * Wrap a plugin's element component, and its sub-plugins', in the block error boundary (GH #2809), so one block
 * that throws shows a card instead of taking the editor down. Leaves are marks, not blocks, and are left alone.
 */
export const guardPluginNode = <P extends GuardablePlugin>(plugin: P): P => {
  const component = plugin.node?.component
  const guardsNode = Boolean(component) && !plugin.node?.isLeaf
  const plugins = plugin.plugins?.map(guardPluginNode)
  if (!guardsNode && !plugins) {
    return plugin
  }
  return {
    ...plugin,
    ...(plugins ? { plugins } : {}),
    ...(guardsNode && plugin.node
      ? {
          node: {
            ...plugin.node,
            component: withBlockErrorBoundary(component as ComponentType<object>, {
              label: labelOf(plugin),
              inline: Boolean(plugin.node.isInline),
            }),
          },
        }
      : {}),
  }
}
